import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { publicApi } from '../services/api';

interface CaptchaState {
  question: string;
  answer: number;
}

const createCaptcha = (): CaptchaState => {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return {
    question: `What is ${a} + ${b}?`,
    answer: a + b,
  };
};

const AccountDeletionRequest: React.FC = () => {
  const [email, setEmail] = useState('');
  const [captcha, setCaptcha] = useState<CaptchaState | null>(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetCaptcha = useCallback(() => {
    setCaptcha(createCaptcha());
    setCaptchaInput('');
  }, []);

  useEffect(() => {
    resetCaptcha();
  }, [resetCaptcha]);

  const validateEmail = (value: string) => {
    // Simple email format validation
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!validateEmail(email)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    if (!captcha || Number.isNaN(Number(captchaInput))) {
      setErrorMessage('Please answer the CAPTCHA question to confirm you are not a robot.');
      return;
    }

    if (Number(captchaInput) !== captcha.answer) {
      setErrorMessage('The CAPTCHA answer is incorrect. Please try again.');
      resetCaptcha();
      return;
    }

    try {
      setSubmitting(true);
      await publicApi.requestAccountDeletion({
        email,
        // This field can be used by the backend to distinguish CAPTCHA type
        captchaAnswer: captchaInput,
      });

      setSuccessMessage(
        'If an account exists for this email address, we’ve sent you an email with a link to confirm your deletion request.'
      );
      setEmail('');
      resetCaptcha();
    } catch (error) {
      setErrorMessage(
        'Something went wrong while submitting your request. Please try again in a moment.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: (theme) => theme.palette.grey[100],
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={3}>
          <CardContent>
            <Stack spacing={3} alignItems="center">
            <img
                src="/dictomate_logo.png"
                alt="Dictomate Logo"
                style={{
                  maxWidth: '200px',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
              <Typography variant="h4" component="h1" align="center">
                Request to delete your Dictomate account
              </Typography>
              <Typography variant="body1" align="center" color="text.secondary">
                We know that sometimes you need to move on. This page lets you request deletion of
                your Dictomate account in a secure way.
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary">
                After you submit your email, we&apos;ll send you a confirmation message with a
                secure link. Your account will only be scheduled for deletion once you click that
                link, and the link will expire after a short time for your protection.
              </Typography>

              {successMessage && (
                <Alert severity="success" sx={{ width: '100%' }}>
                  {successMessage}
                </Alert>
              )}

              {errorMessage && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {errorMessage}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 1 }}>
                <Stack spacing={2}>
                  <TextField
                    label="Email address"
                    type="email"
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />

                  {captcha && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        CAPTCHA verification
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Please answer this simple question to confirm you are a real person.
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                        <Typography sx={{ minWidth: 130 }}>{captcha.question}</Typography>
                        <TextField
                          label="Your answer"
                          value={captchaInput}
                          onChange={(e) => setCaptchaInput(e.target.value)}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          sx={{ maxWidth: 160 }}
                          required
                        />
                        <Button
                          type="button"
                          onClick={resetCaptcha}
                          variant="outlined"
                          size="small"
                        >
                          New question
                        </Button>
                      </Stack>
                    </Box>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    sx={{ mt: 1 }}
                  >
                    {submitting ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} color="inherit" />
                        <span>Sending request…</span>
                      </Stack>
                    ) : (
                      'Send deletion request email'
                    )}
                  </Button>

                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default AccountDeletionRequest;




